import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';

import { hideStickyMenus, showParticipantStickyMenu } from '../../store/formsState';
import { loadParticipantSectionHeadings } from '../../store/participantService';
import AllActiveCourseAndProvisionManagement from '../courses/allActiveCourseAndProvision/AllActiveCourseAndProvisionManagement';
import ParticipantStickyMenu from '../navigation/participant/ParticipantStickyMenu';
import AccordionHeader from '../ui/accordion/AccordionHeader';
import AccordionIcon from '../ui/accordion/AccordionIcon';

import Education from './education/Education';
import Submissions from './submissions/Submissions';
import SupportedJobSearchManagement from './supportedJobSearch/SupportedJobSearchManagement';
import WorkHistory from './workHistory/WorkHistory';
import WorkRequirements from './workRequirements/WorkRequirements';

import app from '../../app.module.css';

const CourseWorkAndEducation = () => {
    const dispatch = useDispatch();

    //  LOCAL STATE
    const initialState = {
        courses: false,
        workRequirements: false,
        workHistory: false,
        education: false,
        supportedJobSearch: false,
        submissions: false
    };
    const [isSection, setIsSection] = useState(initialState);
    const [expanded, setExpanded] = useState('');

    // STORE STATE
    const currentParticipant = useSelector(
        (state) => state.entities.participantService.currentParticipant
    );
    const participantSectionHeadings = useSelector(
        (state) => state.entities.participantService.participantSectionHeadings
    );

    //  USE EFFECTS
    useEffect(() => {
        dispatch(showParticipantStickyMenu());
        return () => {
            dispatch(hideStickyMenus());
        };
    }, []);

    useEffect(() => {
        if (!('contractId' in currentParticipant)) return;
        dispatch(loadParticipantSectionHeadings(currentParticipant.contractId));
    }, [currentParticipant]);

    useEffect(() => {
        if (Object.keys(participantSectionHeadings).length < 1) return;
        const sections = (({
            courses,
            workRequirements,
            workHistory,
            education,
            supportedJobSearch,
            submissions
        }) => ({
            courses,
            workRequirements,
            workHistory,
            education,
            supportedJobSearch,
            submissions
        }))(participantSectionHeadings);
        setIsSection(sections);
    }, [participantSectionHeadings]);

    // FNs
    const onPanelChange = (panel) => setExpanded(expanded === panel ? '' : panel);

    // RENDER
    return (
        <div className={app.withSideBar}>
            <div className={app.pageContainer}>
                <h1 className={app.mainHeading}>Course, Work & Education</h1>
                <div hidden={!isSection.courses} className={app.accordionDivider}>
                    <Accordion
                        expanded={expanded === 'courses'}
                        onChange={() => onPanelChange('courses')}
                        slotProps={{ transition: { mountOnEnter: true } }}>
                        <AccordionSummary
                            expandIcon={<AccordionIcon />}
                            aria-controls="courses-content"
                            id="courses-header">
                            <AccordionHeader>Courses</AccordionHeader>
                        </AccordionSummary>
                        <AccordionDetails>
                            {expanded === 'courses' && <AllActiveCourseAndProvisionManagement />}
                        </AccordionDetails>
                    </Accordion>
                </div>
                <div hidden={!isSection.workRequirements} className={app.accordionDivider}>
                    <Accordion
                        expanded={expanded === 'work requirements'}
                        onChange={() => onPanelChange('work requirements')}
                        slotProps={{ transition: { mountOnEnter: true } }}>
                        <AccordionSummary
                            expandIcon={<AccordionIcon />}
                            aria-controls="workRequirements-content"
                            id="workRequirements-header">
                            <AccordionHeader>Work Requirements</AccordionHeader>
                        </AccordionSummary>
                        <AccordionDetails>
                            {expanded === 'work requirements' && <WorkRequirements />}
                        </AccordionDetails>
                    </Accordion>
                </div>
                <div hidden={!isSection.supportedJobSearch} className={app.accordionDivider}>
                    <Accordion
                        expanded={expanded === 'supported job search'}
                        onChange={() => onPanelChange('supported job search')}
                        slotProps={{ transition: { mountOnEnter: true } }}>
                        <AccordionSummary
                            expandIcon={<AccordionIcon />}
                            aria-controls="supportedJobSearch-content"
                            id="supportedJobSearch-header">
                            <AccordionHeader>Supported Job Search</AccordionHeader>
                        </AccordionSummary>
                        <AccordionDetails>
                            {expanded === 'supported job search' && (
                                <SupportedJobSearchManagement />
                            )}
                        </AccordionDetails>
                    </Accordion>
                </div>
                <div hidden={!isSection.workHistory} className={app.accordionDivider}>
                    <Accordion
                        expanded={expanded === 'work history'}
                        onChange={() => onPanelChange('work history')}
                        slotProps={{ transition: { mountOnEnter: true } }}>
                        <AccordionSummary
                            expandIcon={<AccordionIcon />}
                            aria-controls="workHistory-content"
                            id="workHistory-header">
                            <AccordionHeader>Work History</AccordionHeader>
                        </AccordionSummary>
                        <AccordionDetails>
                            {expanded === 'work history' && <WorkHistory />}
                        </AccordionDetails>
                    </Accordion>
                </div>
                <div hidden={!isSection.education} className={app.accordionDivider}>
                    <Accordion
                        expanded={expanded === 'education'}
                        onChange={() => onPanelChange('education')}
                        slotProps={{ transition: { mountOnEnter: true } }}>
                        <AccordionSummary
                            expandIcon={<AccordionIcon />}
                            aria-controls="education-content"
                            id="education-header">
                            <AccordionHeader>Education</AccordionHeader>
                        </AccordionSummary>
                        <AccordionDetails>
                            {expanded === 'education' && <Education />}
                        </AccordionDetails>
                    </Accordion>
                </div>
                <div hidden={!isSection.submissions} className={app.accordionDivider}>
                    <Accordion
                        expanded={expanded === 'submissions'}
                        onChange={() => onPanelChange('submissions')}
                        slotProps={{ transition: { mountOnEnter: true } }}>
                        <AccordionSummary
                            expandIcon={<AccordionIcon />}
                            aria-controls="submissions-content"
                            id="submissions-header">
                            <AccordionHeader>Submissions</AccordionHeader>
                        </AccordionSummary>
                        <AccordionDetails>
                            {expanded === 'submissions' && <Submissions />}
                        </AccordionDetails>
                    </Accordion>
                </div>
            </div>
            <div className={app.sideBar}>
                <ParticipantStickyMenu />
            </div>
        </div>
    );
};

export default CourseWorkAndEducation;
